/**
 * Pure calculation functions used by the admin dashboard.
 * Extracted to enable unit testing and ensure data consistency.
 */

/**
 * Calculates a percentage of a part relative to a total.
 * Returns 0 when the total is zero to avoid division-by-zero.
 */
export function safePercentage(part: number, total: number): number {
  if (total <= 0) return 0
  return Math.round((part / total) * 100)
}

/**
 * Computes the completion rate from review metrics.
 * Completion rate = completed / (completed + inProgress + skipped).
 */
export function completionRate(completed: number, inProgress: number, skipped: number): number {
  const total = completed + inProgress + skipped
  return safePercentage(completed, total)
}

/**
 * Computes the total activity (sum of completed, inProgress, skipped reviews).
 */
export function totalActivity(completed: number, inProgress: number, skipped: number): number {
  return completed + inProgress + skipped
}

/**
 * Computes talent rank percentages from individual counts.
 * Returns an object with each rank's percentage of the total ranked population.
 * All percentages are rounded integers. When total is 0, all return '–'.
 */
export function talentRankPercentages(
  tenX: number,
  nineX: number,
  core: number,
  mismatch: number
): { tenX: string; nineX: string; core: string; mismatch: string } {
  const total = tenX + nineX + core + mismatch
  if (total === 0) {
    return { tenX: '–', nineX: '–', core: '–', mismatch: '–' }
  }
  return {
    tenX: `${Math.round((tenX / total) * 100)}%`,
    nineX: `${Math.round((nineX / total) * 100)}%`,
    core: `${Math.round((core / total) * 100)}%`,
    mismatch: `${Math.round((mismatch / total) * 100)}%`,
  }
}

/**
 * Computes the rank-mix percentage for a single segment in a stacked bar.
 * Used by the LeaderBreakdownTable's RankMiniBar.
 */
export function rankMixPercentage(segmentCount: number, total: number): number {
  if (total <= 0) return 0
  return Math.round((segmentCount / total) * 100)
}

/**
 * Paginates an array client-side.
 * Returns the slice for the requested page and the total number of pages.
 */
export function paginate<T>(items: T[], page: number, pageSize: number): { paged: T[]; totalPages: number } {
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize))
  const safePage = Math.max(0, Math.min(page, totalPages - 1))
  const paged = items.slice(safePage * pageSize, (safePage + 1) * pageSize)
  return { paged, totalPages }
}
